import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getOktaAuth, getStoredOktaClientId } from "@/services/oktaAuth";
import { useOktaLogonMutation } from "@/services/authApi";

type Status = "idle" | "handling" | "success" | "error";

function toRelativeUrl(url: string, origin: string) {
    return url.startsWith(origin) ? url.slice(origin.length) || "/" : url;
}

export function useOktaCallbackHandler(options?: { onSuccess?: () => void; onError?: (message: string) => void }) {
    const navigate = useNavigate();
    const [oktaLogon] = useOktaLogonMutation();

    const [status, setStatus] = useState<Status>("idle");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleOktaCallback = useCallback(async () => {
        setStatus("handling");
        setErrorMessage(null);

        try {
            const clientId = getStoredOktaClientId();
            if (!clientId) {
                throw new Error("Okta client ID not found. Please sign in again from the login page.");
            }

            const oktaAuth = getOktaAuth(clientId);

            if (!oktaAuth.isLoginRedirect()) {
                setStatus("error");
                setErrorMessage("Not an Okta redirect callback.");
                return;
            }

            // Parse code from URL
            const params = new URLSearchParams(window.location.search);
            const code = params.get("code");
            const state = params.get("state");

            if (!code) {
                throw new Error("Authorization code missing from redirect.");
            }

            // Get code_verifier from Okta's stored transaction (saved at signInWithRedirect)
            const meta = await oktaAuth.transactionManager.load();
            const codeVerifier = (meta as { codeVerifier?: string } | null)?.codeVerifier;
            await oktaAuth.transactionManager.clear();

            if (!codeVerifier) {
                throw new Error("PKCE code_verifier not found. Sign-in may have been initiated from a different tab or session.");
            }

            const redirectUri = oktaAuth.options.redirectUri ?? `${window.location.origin}/oauth2/callback`;

            // Backend exchanges code for tokens and returns Pulse JWT
            await oktaLogon({
                code,
                codeVerifier,
                redirectUri,
                clientId,
            }).unwrap();

            // Restore where the user was before Okta redirect
            const originalUri = state ? await oktaAuth.getOriginalUri(state) : undefined;
            oktaAuth.removeOriginalUri();

            const next = originalUri
                ? toRelativeUrl(originalUri, window.location.origin)
                : "/";

            options?.onSuccess?.();
            setStatus("success");
            navigate(next, { replace: true });
        } catch (e: any) {
            setStatus("error");
            setErrorMessage(e?.message || "Failed to complete Okta sign-in.");
            options?.onError?.(e?.message || "Failed to complete Okta sign-in.");
        }
    }, [navigate, oktaLogon]);

    return { status, errorMessage, handleOktaCallback };
}